"use client";

/**
 * BalanceList — list-based replacement for the legacy PetalLifeWheel +
 * Area Emphasis + Allocation Gap + Needs Protection + Over-absorbing
 * sections of `BalanceReviewScreen`. Implements Variant A ("Paired") from
 * the design handoff at /tmp/tin_handoff/design_handoff_balance_review.
 *
 * Two sort modes (purely visual; do NOT affect scheduling — see
 * `frontend/lib/balanceListPreferences.ts` for the rationale):
 *
 *   - "gap"      (default): committed-minus-scheduled descending. The
 *                most-neglected area surfaces first. Self-balancing.
 *   - "priority" (drag-reorder): user-curated order, persisted in
 *                localStorage. Areas not yet pinned sink to the bottom.
 *
 * In priority mode, clicking "Reorder" exposes a drag handle in the
 * left rail (HTML5 native drag) plus up/down buttons (touch/keyboard
 * fallback). Each reorder writes the new map to localStorage.
 *
 * Scheduler signals are commit_time + gap_ratio only. Priority is local.
 * If a future requirement proposes a backend `priority` column, push
 * back: it adds a second lever that says the same thing as commit_time
 * and creates a "where do I tweak this?" UX dead-end. See conversation
 * thread 2026-05-06.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import { useAuth } from "@/lib/store";
import {
  comparePriority,
  loadAreaPriority,
  reindexPriority,
  saveAreaPriority,
  type AreaPriorityMap,
} from "@/lib/balanceListPreferences";
import styles from "./balance-list.module.css";

export interface BalanceListAreaGoal {
  id: string;
  title: string;
  /** 0..1 from API (`progress_pct`). */
  progress: number;
}

export interface BalanceListArea {
  id: string;
  name: string;
  icon: AreaIconKind;
  /** Weekly minute target. `null` ⇒ "not committed". */
  target_minutes_per_week: number | null;
  scheduled_minutes_this_week: number;
  primary_goal: BalanceListAreaGoal | null;
}

export type BalanceListSortMode = "gap" | "priority";

export interface BalanceListProps {
  areas: BalanceListArea[];
  /** Fired when "Adjust commitments" is clicked. Host renders the modal. */
  onAdjustClick: () => void;
  /** Optional: row click → navigate to area detail. */
  onAreaClick?: (areaId: string) => void;
  eyebrow?: string;
  title?: string;
}

type AreaIconKind =
  | "career"
  | "finance"
  | "health"
  | "learning"
  | "personal"
  | "relationships"
  | "creative"
  | "default";

/** Sort: gap descending; null-target rows sink to the bottom. */
function sortByGap<T extends BalanceListArea>(areas: T[]): T[] {
  return [...areas].sort((a, b) => {
    const aNull = a.target_minutes_per_week == null;
    const bNull = b.target_minutes_per_week == null;
    if (aNull && !bNull) return 1;
    if (!aNull && bNull) return -1;
    if (aNull && bNull) return a.name.localeCompare(b.name);
    const ga =
      (a.target_minutes_per_week ?? 0) - (a.scheduled_minutes_this_week ?? 0);
    const gb =
      (b.target_minutes_per_week ?? 0) - (b.scheduled_minutes_this_week ?? 0);
    return gb - ga;
  });
}

/** Sort by persisted priority map. Null-target rows still sink. */
function sortByPriority<T extends BalanceListArea>(
  areas: T[],
  priority: AreaPriorityMap,
): T[] {
  return [...areas].sort((a, b) => {
    const aNull = a.target_minutes_per_week == null;
    const bNull = b.target_minutes_per_week == null;
    if (aNull && !bNull) return 1;
    if (!aNull && bNull) return -1;
    return comparePriority(priority, a.id, b.id);
  });
}

/** Format minutes as "Xh Ym" / "Xm" / "—". */
export function fmt(m: number | null | undefined): string {
  if (m == null) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

interface AreaIconProps {
  kind: AreaIconKind;
  size?: number;
}

export function AreaIcon({ kind, size = 18 }: AreaIconProps) {
  const s = {
    width: size,
    height: size,
    strokeWidth: 1.5,
    fill: "none" as const,
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (kind) {
    case "career":
      return (
        <svg viewBox="0 0 24 24" {...s}>
          <path d="M3 8h18v11H3z" />
          <path d="M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
          <path d="M3 13h18" />
        </svg>
      );
    case "finance":
      return (
        <svg viewBox="0 0 24 24" {...s}>
          <path d="M12 3v18" />
          <path d="M16 7H9.5a2.5 2.5 0 0 0 0 5H14a2.5 2.5 0 0 1 0 5H7" />
        </svg>
      );
    case "health":
      return (
        <svg viewBox="0 0 24 24" {...s}>
          <path d="M12 21s-7-4.5-7-11a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 6.5-7 11-7 11z" />
        </svg>
      );
    case "learning":
      return (
        <svg viewBox="0 0 24 24" {...s}>
          <path d="M3 5l9-2 9 2v14l-9-2-9 2z" />
          <path d="M12 3v18" />
        </svg>
      );
    case "personal":
      return (
        <svg viewBox="0 0 24 24" {...s}>
          <path d="M12 22V10" />
          <path d="M12 10c0-3 2-5 5-5-1 4-3 5-5 5z" />
          <path d="M12 12c0-3-2-5-5-5 1 4 3 5 5 5z" />
        </svg>
      );
    case "relationships":
      return (
        <svg viewBox="0 0 24 24" {...s}>
          <circle cx="9" cy="8" r="3" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
          <path d="M14 20c0-2.5 1.5-4.5 4-4.5s4 2 4 4.5" />
        </svg>
      );
    case "creative":
      return (
        <svg viewBox="0 0 24 24" {...s}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="8" cy="10" r="1.2" />
          <circle cx="16" cy="10" r="1.2" />
          <circle cx="10" cy="15" r="1.2" />
          <circle cx="15" cy="15" r="1.2" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" {...s}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
}

function HandUnderline({ width = 64 }: { width?: number }) {
  return (
    <svg
      className={styles["bl-flourish"]}
      width={width}
      height={6}
      viewBox={`0 0 ${width} 6`}
      aria-hidden
    >
      <path
        d={`M2 4 Q ${width * 0.25} 1, ${width * 0.5} 3.5 T ${width - 2} 2.5`}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeLinecap="round"
      />
    </svg>
  );
}

function DragGrip() {
  return (
    <span aria-hidden className={styles["bl-drag-rail"]}>
      <span className={styles["bl-drag-grip-dot"]} />
      <span className={styles["bl-drag-grip-dot"]} />
      <span className={styles["bl-drag-grip-dot"]} />
      <span className={styles["bl-drag-grip-dot"]} />
      <span className={styles["bl-drag-grip-dot"]} />
      <span className={styles["bl-drag-grip-dot"]} />
    </span>
  );
}

export function areaKeyToIcon(areaKey: string): AreaIconKind {
  const k = areaKey.toLowerCase();
  if (k.includes("career") || k.includes("work") || k.includes("professional"))
    return "career";
  if (k.includes("finance") || k.includes("money")) return "finance";
  if (k.includes("health") || k.includes("body") || k.includes("fitness"))
    return "health";
  if (k.includes("learn") || k.includes("study") || k.includes("growth"))
    return "learning";
  if (k.includes("personal") || k.includes("self")) return "personal";
  if (
    k.includes("relation") ||
    k.includes("family") ||
    k.includes("friend") ||
    k.includes("social")
  )
    return "relationships";
  if (k.includes("creat") || k.includes("art") || k.includes("hobby"))
    return "creative";
  return "default";
}

export default function BalanceList({
  areas,
  onAdjustClick,
  onAreaClick,
  eyebrow = "This week's balance",
  title = "What did you promise — and how much fits?",
}: BalanceListProps) {
  const userId = useAuth((s) => s.user?.id ?? null);

  const [sortMode, setSortMode] = useState<BalanceListSortMode>("gap");
  const [reorderable, setReorderable] = useState(false);
  const [priorityMap, setPriorityMap] = useState<AreaPriorityMap>({});
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<{
    id: string;
    side: "above" | "below";
  } | null>(null);

  // Hydrate from localStorage once we know which user is signed in.
  useEffect(() => {
    setPriorityMap(loadAreaPriority(userId));
  }, [userId]);

  const sorted = useMemo(() => {
    if (sortMode === "priority") return sortByPriority(areas, priorityMap);
    return sortByGap(areas);
  }, [areas, priorityMap, sortMode]);

  const maxTarget = useMemo(
    () => Math.max(...sorted.map((a) => a.target_minutes_per_week || 0), 60),
    [sorted],
  );

  const persistOrder = useCallback(
    (ids: string[]) => {
      const next = reindexPriority(ids);
      setPriorityMap(next);
      saveAreaPriority(userId, next);
    },
    [userId],
  );

  const moveTo = useCallback(
    (sourceId: string, targetIndex: number) => {
      const ids = sorted.map((a) => a.id);
      const sourceIdx = ids.indexOf(sourceId);
      if (sourceIdx === -1) return;
      const next = [...ids];
      next.splice(sourceIdx, 1);
      next.splice(Math.max(0, Math.min(next.length, targetIndex)), 0, sourceId);
      persistOrder(next);
    },
    [sorted, persistOrder],
  );

  const moveUp = useCallback(
    (id: string) => {
      const ids = sorted.map((a) => a.id);
      const idx = ids.indexOf(id);
      if (idx <= 0) return;
      const next = [...ids];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      persistOrder(next);
    },
    [sorted, persistOrder],
  );
  const moveDown = useCallback(
    (id: string) => {
      const ids = sorted.map((a) => a.id);
      const idx = ids.indexOf(id);
      if (idx === -1 || idx >= ids.length - 1) return;
      const next = [...ids];
      [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
      persistOrder(next);
    },
    [sorted, persistOrder],
  );

  const onDragStart = (id: string) => (e: DragEvent<HTMLLIElement>) => {
    if (!reorderable) return;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox to actually fire dragover on the targets.
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (id: string) => (e: DragEvent<HTMLLIElement>) => {
    if (!reorderable || !draggingId || draggingId === id) return;
    e.preventDefault();
    const rect = (e.currentTarget as HTMLLIElement).getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    setDropTargetId({
      id,
      side: e.clientY < midpoint ? "above" : "below",
    });
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setDropTargetId(null);
  };
  const onDrop = (id: string) => (e: DragEvent<HTMLLIElement>) => {
    if (!reorderable || !draggingId || draggingId === id) return;
    e.preventDefault();
    const ids = sorted.map((a) => a.id);
    const targetIdx = ids.indexOf(id);
    const isBelow =
      dropTargetId?.id === id && dropTargetId.side === "below";
    moveTo(draggingId, targetIdx + (isBelow ? 1 : 0));
    setDraggingId(null);
    setDropTargetId(null);
  };

  return (
    <section className={styles["bl-shell"]} data-testid="balance-list-shell">
      <header className={styles["bl-head"]}>
        <div className={styles["bl-eyebrow"]}>{eyebrow}</div>
        <h2 className={styles["bl-title"]}>{title}</h2>
        <button
          type="button"
          className={styles["bl-adjust"]}
          onClick={onAdjustClick}
          data-testid="balance-list-adjust-button"
        >
          Adjust commitments
        </button>
      </header>

      <div className={styles["bl-controls"]}>
        <div
          className={styles["bl-sort-group"]}
          role="tablist"
          aria-label="Sort order"
        >
          <button
            type="button"
            role="tab"
            aria-selected={sortMode === "gap"}
            onClick={() => {
              setSortMode("gap");
              setReorderable(false);
            }}
            className={`${styles["bl-sort-btn"]} ${
              sortMode === "gap" ? styles["bl-sort-btn-active"] : ""
            }`}
            data-testid="balance-list-sort-gap"
          >
            Largest gap
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={sortMode === "priority"}
            onClick={() => setSortMode("priority")}
            className={`${styles["bl-sort-btn"]} ${
              sortMode === "priority" ? styles["bl-sort-btn-active"] : ""
            }`}
            data-testid="balance-list-sort-priority"
          >
            My order
          </button>
        </div>
        {sortMode === "priority" ? (
          <button
            type="button"
            onClick={() => setReorderable((v) => !v)}
            className={`${styles["bl-reorder-btn"]} ${
              reorderable ? styles["bl-reorder-btn-active"] : ""
            }`}
            data-testid="balance-list-reorder-toggle"
          >
            {reorderable ? "Done" : "Reorder…"}
          </button>
        ) : null}
      </div>

      <ol className={styles["bl-list"]}>
        {sorted.map((a, i) => {
          const target = a.target_minutes_per_week;
          const sched = a.scheduled_minutes_this_week ?? 0;
          const isNull = target == null;
          const widthPct = isNull ? 30 : (target / maxTarget) * 100;
          const fillPct = isNull
            ? 0
            : Math.min(100, (sched / Math.max(1, target)) * 100);
          const gap = isNull ? 0 : Math.max(0, target - sched);
          const onTrack =
            !isNull && gap <= Math.max(15, (target ?? 0) * 0.05);
          const zero = !isNull && sched === 0;

          let stateLabel: string;
          if (isNull) stateLabel = "Not committed";
          else if (zero) stateLabel = "Needs attention";
          else if (onTrack) stateLabel = "On track";
          else stateLabel = `${fmt(gap)} short`;

          const stateClass = isNull
            ? "is-null"
            : zero
              ? "is-attn"
              : onTrack
                ? "is-ok"
                : "is-half";

          const isDragging = draggingId === a.id;
          const dropClass =
            dropTargetId?.id === a.id
              ? dropTargetId.side === "above"
                ? styles["is-drop-target-above"]
                : styles["is-drop-target-below"]
              : "";
          const reorderableClass = reorderable
            ? styles["bl-row-reorderable"]
            : "";
          const draggingClass = isDragging ? styles["is-dragging"] : "";

          const handleKey = (e: KeyboardEvent<HTMLLIElement>) => {
            if (reorderable) return; // arrow buttons handle in reorder mode
            if (!onAreaClick) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onAreaClick(a.id);
            }
          };

          return (
            <li
              key={a.id}
              draggable={reorderable}
              onDragStart={onDragStart(a.id)}
              onDragOver={onDragOver(a.id)}
              onDragEnd={onDragEnd}
              onDrop={onDrop(a.id)}
              className={`${styles["bl-row"]} ${styles[stateClass]} ${reorderableClass} ${draggingClass} ${dropClass}`}
              style={{ animationDelay: `${i * 80}ms` }}
              tabIndex={0}
              role={onAreaClick && !reorderable ? "link" : undefined}
              aria-label={`${a.name}, ${stateLabel}, ${fmt(sched)} of ${fmt(target)} scheduled`}
              onClick={
                onAreaClick && !reorderable
                  ? () => onAreaClick(a.id)
                  : undefined
              }
              onKeyDown={handleKey}
              data-testid={`balance-list-row-${a.id}`}
            >
              {reorderable ? <DragGrip /> : null}

              <div className={styles["bl-row-top"]}>
                <div className={styles["bl-id"]}>
                  <span className={styles["bl-icon"]} aria-hidden>
                    <AreaIcon kind={a.icon} />
                  </span>
                  <span className={styles["bl-name"]}>{a.name}</span>
                  {zero && <HandUnderline width={64} />}
                </div>
                <div className={styles["bl-stats"]}>
                  <span className={styles["bl-stat-sched"]}>{fmt(sched)}</span>
                  <span className={styles["bl-stat-sep"]}>/</span>
                  <span className={styles["bl-stat-target"]}>
                    {isNull ? "no target" : fmt(target)}
                  </span>
                  {reorderable ? (
                    <span className={styles["bl-up-down"]}>
                      <button
                        type="button"
                        className={styles["bl-arrow-btn"]}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveUp(a.id);
                        }}
                        disabled={i === 0}
                        aria-label={`Move ${a.name} up`}
                        data-testid={`balance-list-up-${a.id}`}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className={styles["bl-arrow-btn"]}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveDown(a.id);
                        }}
                        disabled={i === sorted.length - 1}
                        aria-label={`Move ${a.name} down`}
                        data-testid={`balance-list-down-${a.id}`}
                      >
                        ↓
                      </button>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className={styles["bl-bar-wrap"]} aria-hidden>
                <div
                  className={styles["bl-bar-track"]}
                  style={{ width: `${widthPct}%` }}
                >
                  <div
                    className={styles["bl-bar-fill"]}
                    style={
                      {
                        width: `${fillPct}%`,
                        "--bl-fill-final": `${fillPct}%`,
                      } as React.CSSProperties
                    }
                  />
                  {!isNull && !zero && !onTrack && (
                    <div
                      className={styles["bl-bar-gap-label"]}
                      style={{ left: `${fillPct}%` }}
                    >
                      <span>{fmt(gap)} gap</span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles["bl-row-bottom"]}>
                <span className={`${styles["bl-state"]} ${styles[stateClass]}`}>
                  {stateLabel}
                </span>
                {a.primary_goal ? (
                  <span className={styles["bl-goal"]}>
                    <span className={styles["bl-goal-arrow"]}>→</span>{" "}
                    {a.primary_goal.title}
                    <span className={styles["bl-goal-pct"]}>
                      {Math.round(a.primary_goal.progress * 100)}%
                    </span>
                  </span>
                ) : (
                  <span
                    className={`${styles["bl-goal"]} ${styles["bl-goal-empty"]}`}
                  >
                    no primary goal set
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
